import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import Product from '../../components/Product/Product';
import getImageUrl from '../../utils/getImageUrl';
import slugify from '../../utils/slugify';
import HomeSkeleton from './HomeSkeleton';
import MexaLoader from '../../components/MexaLoader/MexaLoader';

const Home = ({ categoryItems = [], brandItems = [], productItems = [], toggleMobileSearch, loading = false }) => {
    const navigate = useNavigate();
    const categoryScrollRef = useRef(null);
    const brandsScrollRef = useRef(null);
    const [itemsPerRow, setItemsPerRow] = useState(4);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [displayedProducts, setDisplayedProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const productsPerPage = 12;

    const [showAllCategories, setShowAllCategories] = useState(false);
    const [showAllBrands, setShowAllBrands] = useState(false);

    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateItemsPerRow = () => {
        const container = categoryScrollRef.current || brandsScrollRef.current;
        if (!container) return isMobile ? 4 : 8;

        const containerWidth = container.offsetWidth;
        const itemWidth = 80;
        const gap = isMobile ? 8 : 16;
        const calculatedItems = Math.floor((containerWidth + gap) / (itemWidth + gap));
        return Math.max(3, calculatedItems);
    };

    const isReady = !loading && (categoryItems.length > 0 || brandItems.length > 0 || productItems.length > 0);

    useEffect(() => {
        if (!isReady) return;

        const updateItemsPerRow = () => {
            setItemsPerRow(calculateItemsPerRow());
        };

        // Initial calculation when DOM is ready
        updateItemsPerRow();

        const container = categoryScrollRef.current || brandsScrollRef.current;
        let observer;

        if (container && window.ResizeObserver) {
            observer = new ResizeObserver(() => {
                updateItemsPerRow();
            });
            observer.observe(container);
        } else {
            window.addEventListener('resize', updateItemsPerRow);
        }

        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', updateItemsPerRow);
        };
    }, [isReady, isMobile, categoryItems, brandItems]);

    useEffect(() => {
        if (showAllCategories || showAllBrands) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showAllCategories, showAllBrands]);

    useEffect(() => {
        if (productItems.length === 0) {
            setDisplayedProducts([]);
            setHasMore(false);
            return;
        }

        const startIndex = 0;
        const endIndex = currentPage * productsPerPage;
        const productsToShow = productItems.slice(startIndex, endIndex);

        setDisplayedProducts(productsToShow);
        setHasMore(endIndex < productItems.length);
    }, [productItems, currentPage]);

    const handleCategoryClick = (category) => {
        if (!category) return;
        navigate(`/products/${slugify(category.slag || category.name)}`);
    };

    const handleBrandClick = (brand) => {
        if (!brand) return;
        navigate(`/brand/${slugify(brand.slag || brand.name)}`);
    };

    const getSectionLayout = (items) => {
        if (!Array.isArray(items)) return { row1: [], row2: [], hasMore: false };
        const maxRows = isMobile ? 1 : 2;
        const maxSlots = itemsPerRow * maxRows;
        const hasMore = items.length > maxSlots;
        const visibleItems = hasMore ? items.slice(0, maxSlots - 1) : items;

        if (maxRows === 1) {
            return {
                row1: visibleItems,
                row2: [],
                hasMore,
            };
        }

        return {
            row1: visibleItems.slice(0, itemsPerRow),
            row2: visibleItems.slice(itemsPerRow),
            hasMore,
        };
    };

    const categoryLayout = getSectionLayout(categoryItems);
    const brandLayout = getSectionLayout(brandItems);

    const renderCategoryItem = (item, key) => (
        <div
            key={item?.id ?? key}
            onClick={() => handleCategoryClick(item)}
            className="flex-shrink-0 w-20 text-center cursor-pointer select-none group"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCategoryClick(item); }}
        >
            <div className="w-16 h-16 mx-auto rounded-lg flex items-center justify-center overflow-hidden mb-2 p-1.5 bg-gray-50 border border-gray-100 shadow-sm group-hover:shadow-md transition-shadow">
                {item?.img_path ? (
                    <img
                        src={getImageUrl(item?.img_path)}
                        alt={item?.slug ?? item?.name ?? ''}
                        className="w-full h-full object-contain pointer-events-none mix-blend-multiply"
                        loading="lazy"
                        onError={(e) => { e.target.onerror = null; e.target.src = "/images/productimgmiss.png"; }}
                    />
                ) : (
                    <div className="text-xs text-gray-500">No Image</div>
                )}
            </div>
            <span className="text-[10px] sm:text-xs truncate px-1 text-gray-600 font-medium block">
                {item?.name}
            </span>
        </div>
    );

    const renderBrandItem = (brand, key) => (
        <div
            key={brand?.id ?? key}
            onClick={() => handleBrandClick(brand)}
            className="flex-shrink-0 w-20 text-center cursor-pointer select-none group"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') handleBrandClick(brand); }}
        >
            <div className="w-16 h-16 mx-auto rounded-lg flex items-center justify-center overflow-hidden mb-2 p-1.5 bg-white border border-gray-100 shadow-sm group-hover:shadow-md transition-shadow">
                {brand?.img_path ? (
                    <img
                        src={getImageUrl(brand?.img_path)}
                        alt={brand?.slug ?? brand?.name ?? ''}
                        className="w-full h-full object-contain pointer-events-none mix-blend-multiply"
                        loading="lazy"
                        onError={(e) => { e.target.onerror = null; e.target.src = "/images/productimgmiss.png"; }}
                    />
                ) : (
                    <div className="text-xs text-gray-500">No Image</div>
                )}
            </div>
            <span className="text-[10px] sm:text-xs truncate px-1 text-gray-600 font-medium block">
                {brand?.name}
            </span>
        </div>
    );

    const renderMoreButton = (onClick) => (
        <div
            className="flex-shrink-0 w-20 text-center cursor-pointer select-none group"
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
        >
            <div className="w-16 h-16 mx-auto rounded-lg flex items-center justify-center overflow-hidden mb-2 p-1 bg-gray-50 border border-gray-200 shadow-sm text-teal-600 group-hover:shadow-md transition-shadow">
                <i className="fas fa-chevron-right text-xl"></i>
            </div>
            <div className="text-[10px] sm:text-xs text-teal-700 font-bold truncate px-1">
                More
            </div>
        </div>
    );

    const loadMoreProducts = () => {
        if (loadingMore || !hasMore) return;

        setLoadingMore(true);

        setTimeout(() => {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            setLoadingMore(false);
        }, 300);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (loadingMore || !hasMore) return;

            // Trigger when within 100px of the bottom
            if (window.innerHeight + Math.round(document.documentElement.scrollTop) >=
                document.documentElement.offsetHeight - 100) {
                loadMoreProducts();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingMore, hasMore, currentPage]);

    // --- BANNER CAROUSEL ---
    const banners = [
        {
            mobile: '/images/home-banner.png',
            desktop: '/images/home-banner-destop.png',
            alt: 'Home Banner',
        },
        {
            // Use existing file name in public/images
            mobile: '/images/meXa_medico_mobile_banner.png',
            desktop: '/images/home-banner-destop.png',
            alt: 'Medical Banner',
        },
    ];

    const [currentSlide, setCurrentSlide] = useState(0);
    const [isCarouselPaused, setIsCarouselPaused] = useState(false);
    const [carouselInterval, setCarouselInterval] = useState(window.innerWidth >= 768 ? 3000 : 2000);

    useEffect(() => {
        const handleResize = () => {
            setCarouselInterval(window.innerWidth >= 768 ? 3000 : 2000);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!banners || banners.length <= 1) return;
        if (isCarouselPaused) return;

        const id = setInterval(() => {
            setCurrentSlide((s) => (s + 1) % banners.length);
        }, carouselInterval);

        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCarouselPaused, banners.length, carouselInterval]);

    if (loading || (categoryItems.length === 0 && brandItems.length === 0 && productItems.length === 0)) {
        return <HomeSkeleton />;
    }

    return (
        <main className="bg-white min-h-screen pb-2">
            <div className="w-full mb-2">
                {banners && banners.length > 0 && (
                    <picture
                        onMouseEnter={() => setIsCarouselPaused(true)}
                        onMouseLeave={() => setIsCarouselPaused(false)}
                        onTouchStart={() => setIsCarouselPaused(true)}
                        onTouchEnd={() => setIsCarouselPaused(false)}
                    >
                        <source media="(min-width: 768px)" srcSet={banners[currentSlide].desktop} />
                        <img
                            src={banners[currentSlide].mobile}
                            alt={banners[currentSlide].alt}
                            className="w-full h-auto md:h-76 object-cover mb-2"
                        />
                    </picture>
                )}
            </div>

            <div className="view-section p-4">
                <section className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-dark">Shop by Category</h2>
                            <p className="text-sm text-gray-400">Healthcare essentials</p>
                        </div>
                    </div>

                    <div
                        ref={categoryScrollRef}
                        className="w-full overflow-hidden pb-2 mt-2"
                    >
                        {isMobile ? (
                            <div className="flex justify-start gap-3 sm:gap-4 w-full overflow-hidden items-start">
                                {categoryLayout.row1.map((item, index) => renderCategoryItem(item, index))}
                                {categoryLayout.hasMore && renderMoreButton(() => setShowAllCategories(true))}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                <div className="flex gap-4 items-start">
                                    {categoryLayout.row1.map((item, index) => renderCategoryItem(item, index))}
                                </div>
                                {(categoryLayout.row2.length > 0 || categoryLayout.hasMore) && (
                                    <div className="flex gap-4 items-start">
                                        {categoryLayout.row2.map((item, index) => renderCategoryItem(item, `r2-${index}`))}
                                        {categoryLayout.hasMore && renderMoreButton(() => setShowAllCategories(true))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                <section className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-dark">Featured Brands</h2>
                            <p className="text-sm text-gray-400">Top Brands</p>
                        </div>
                    </div>

                    <div
                        ref={brandsScrollRef}
                        className="w-full overflow-hidden pb-2 mt-2"
                    >
                        {isMobile ? (
                            <div className="flex justify-start gap-3 sm:gap-4 w-full overflow-hidden items-start">
                                {brandLayout.row1.map((brand, index) => renderBrandItem(brand, index))}
                                {brandLayout.hasMore && renderMoreButton(() => setShowAllBrands(true))}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                <div className="flex gap-4 items-start">
                                    {brandLayout.row1.map((brand, index) => renderBrandItem(brand, index))}
                                </div>
                                {(brandLayout.row2.length > 0 || brandLayout.hasMore) && (
                                    <div className="flex gap-4 items-start">
                                        {brandLayout.row2.map((brand, index) => renderBrandItem(brand, `b-r2-${index}`))}
                                        {brandLayout.hasMore && renderMoreButton(() => setShowAllBrands(true))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                <section className="mb-5">
                    <div className="flex items-end justify-between mb-2">
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-dark">Trending Near You</h2>
                            <p className="text-sm text-gray-400">
                                Showing {displayedProducts.length} of {productItems.length} products
                            </p>
                        </div>
                    </div>

                    <div className="grid gap-2 sm:gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6">
                        {displayedProducts.map((product) => (
                            <Product key={product.id} product={product} />
                        ))}
                    </div>

                    {loadingMore && (
                        <div className="flex justify-center py-8">
                            <MexaLoader size="md" inline />
                        </div>
                    )}

                    {hasMore && !loadingMore && (
                        <div className="flex justify-center py-4">
                            <button
                                onClick={loadMoreProducts}
                                className="bg-primary/60 hover:bg-primary/90 text-white px-6 py-2 rounded-md transition-colors"
                            >
                                Load More Products
                            </button>
                        </div>
                    )}

                </section>
            </div>

            {showAllCategories && (
                <div className="fixed inset-0 bg-slate-50 z-[9999] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center shadow-sm z-10 max-w-7xl mx-auto">
                        <button
                            onClick={() => setShowAllCategories(false)}
                            className="mr-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700 transition-colors"
                        >
                            <i className="fas fa-arrow-left text-lg"></i>
                        </button>
                        <h2 className="text-xl md:text-2xl font-bold text-dark">All Categories</h2>
                    </div>
                    <div className="p-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-4 max-w-7xl mx-auto bg-white min-h-screen content-start">
                        {categoryItems.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => {
                                    setShowAllCategories(false);
                                    handleCategoryClick(item);
                                }}
                                className="w-full flex flex-col items-center cursor-pointer select-none group"
                            >
                                <div
                                    className="w-full aspect-square p-2 mb-2 flex items-center justify-center rounded-lg overflow-hidden bg-gray-50 border border-gray-100 group-hover:shadow-md transition-shadow"
                                >
                                    {item.img_path ? (
                                        <img
                                            src={getImageUrl(item.img_path)}
                                            alt={item.name}
                                            className="w-full h-full object-contain pointer-events-none mix-blend-multiply"
                                            onError={(e) => { e.target.onerror = null; e.target.src = "/images/productimgmiss.png"; }}
                                        />
                                    ) : (
                                        <div className="text-xs text-gray-500">No Image</div>
                                    )}
                                </div>
                                <span
                                    className="text-[10px] md:text-xs text-center leading-tight line-clamp-2 w-full px-1 text-gray-600 font-medium"
                                >
                                    {item.name}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showAllBrands && (
                <div className="fixed inset-0 bg-slate-50 z-[9999] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center shadow-sm z-10 max-w-7xl mx-auto">
                        <button
                            onClick={() => setShowAllBrands(false)}
                            className="mr-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700 transition-colors"
                        >
                            <i className="fas fa-arrow-left text-lg"></i>
                        </button>
                        <h2 className="text-xl md:text-2xl font-bold text-dark">All Brands</h2>
                    </div>
                    <div className="p-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-4 max-w-7xl mx-auto bg-white min-h-screen content-start">
                        {brandItems.map((brand) => (
                            <div
                                key={brand.id}
                                onClick={() => {
                                    setShowAllBrands(false);
                                    handleBrandClick(brand);
                                }}
                                className="w-full flex flex-col items-center cursor-pointer select-none group"
                            >
                                <div
                                    className="w-full aspect-square p-2 mb-2 flex items-center justify-center rounded-lg overflow-hidden bg-white border border-gray-100 group-hover:shadow-md transition-shadow"
                                >
                                    {brand.img_path ? (
                                        <img
                                            src={getImageUrl(brand.img_path)}
                                            alt={brand.name}
                                            className="w-full h-full object-contain pointer-events-none mix-blend-multiply"
                                            onError={(e) => { e.target.onerror = null; e.target.src = "/images/productimgmiss.png"; }}
                                        />
                                    ) : (
                                        <div className="text-xs text-gray-500">No Image</div>
                                    )}
                                </div>
                                <span
                                    className="text-[10px] md:text-xs text-center leading-tight line-clamp-2 w-full px-1 text-gray-600 font-medium"
                                >
                                    {brand.name}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </main>
    );
};

export default Home;